import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Grid3x3, Plus, ArrowRight, Users2, Building2, LayoutGrid, Pencil, Trash2,
  XCircle, RefreshCw, FileText, FileType2,
} from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  name: "", sector_id: "", shift_mode: "THREE_SHIFTS",
  description: "", is_active: true, responsible_user_id: null,
};

export default function WorkBoardsPage() {
  const nav = useNavigate();
  const { hasRole } = useAuth();
  const { selected: selectedCompany, selectedCompany: selectedCompanyObj } = useCompany();
  const companyParam = selectedCompany && selectedCompany !== "all" ? selectedCompany : null;
  const canEdit = hasRole("rh_admin", "rh_operacional");
  const canAdmin = hasRole("rh_admin", "master");

  const [boards, setBoards] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const wbParams = { include_inactive: false };
      const secParams = {};
      if (companyParam) {
        wbParams.company_id = companyParam;
        secParams.company_id = companyParam;
      }
      const [{ data: bs }, { data: ss }] = await Promise.all([
        api.get("/work-boards", { params: wbParams }),
        api.get("/sectors", { params: secParams }),
      ]);
      setBoards(bs);
      setSectors(ss);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedCompany]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, sector_id: sectors[0]?.id || "" });
    setOpen(true);
  };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...emptyForm, ...b });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.sector_id) {
      toast.error("Preencha nome e setor");
      return;
    }
    try {
      if (editing) await api.put(`/work-boards/${editing.id}`, form);
      else await api.post("/work-boards", form);
      toast.success(editing ? "Quadro atualizado" : "Quadro criado");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Inativar o quadro "${b.name}"?`)) return;
    try {
      await api.delete(`/work-boards/${b.id}`);
      toast.success("Quadro inativado");
      load();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 409 && detail?.links) {
        const links = detail.links;
        const summary = `Vínculos ativos detectados:\n` +
          `• ${links.employees} colaborador(es) vinculado(s)\n` +
          `• ${links.subsectors} sub-setor(es) ativo(s)\n\n` +
          `Confirmar inativação? Os colaboradores serão desvinculados do quadro.`;
        if (!window.confirm(summary)) return;
        try {
          await api.delete(`/work-boards/${b.id}`, { params: { force: true } });
          toast.success("Quadro inativado e colaboradores desvinculados.");
          load();
        } catch (e2) {
          toast.error(formatApiError(e2.response?.data?.detail));
        }
      } else {
        toast.error(formatApiError(detail));
      }
    }
  };

  const [autoBusy, setAutoBusy] = useState(false);
  const runAutoSync = async () => {
    if (!window.confirm(
      "Deseja gerar/atualizar vagas automáticas para os déficits do quadro?\n\n" +
      "• Cria nova vaga onde quadro aprovado > ativos.\n" +
      "• Ajusta quantidade quando o déficit muda.\n" +
      "• Encerra vagas auto. cujo quadro já está completo."
    )) return;
    setAutoBusy(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      const { data } = await api.post("/quadro-auto/sync", null, { params });
      toast.success(`Sincronização concluída — ${data.created} criada(s), ${data.updated} atualizada(s), ${data.closed} encerrada(s).`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAutoBusy(false); }
  };


  const hardRemove = async (b) => {
    const msg = `Atenção: esta ação excluirá DEFINITIVAMENTE o quadro "${b.name}". ` +
                `Caso existam vínculos com colaboradores, cargos ou histórico, ` +
                `a exclusão não será permitida. Deseja continuar?`;
    if (!window.confirm(msg)) return;
    const confirm2 = window.prompt(`Para confirmar a exclusão PERMANENTE, digite EXCLUIR:`);
    if (confirm2 !== "EXCLUIR") { toast.info("Exclusão cancelada."); return; }
    try {
      await api.delete(`/work-boards/${b.id}/hard`);
      toast.success("Quadro excluído definitivamente.");
      load();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 409) {
        toast.error(typeof detail === "object" ? detail.message : detail || "Bloqueado por vínculos");
      } else {
        toast.error(formatApiError(detail));
      }
    }
  };

  const downloadPdf = async (b) => {
    try {
      const token = localStorage.getItem("rh_token");
      const base = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${base}/api/work-boards/${b.id}/report.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `quadro-${(b.sector_name || b.name || "relatorio").replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Relatório PDF gerado");
    } catch {
      toast.error("Falha ao gerar PDF do quadro");
    }
  };

  const downloadDocx = async (b) => {
    try {
      const token = localStorage.getItem("rh_token");
      const base = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${base}/api/work-boards/${b.id}/report.docx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `quadro-${(b.sector_name || b.name || "relatorio").replace(/\s+/g, "-")}.docx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Relatório Word gerado");
    } catch {
      toast.error("Falha ao gerar Word do quadro");
    }
  };

  return (
    <div className="space-y-6" data-testid="work-boards-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-[#C5A059]/25 bg-gradient-to-br from-[#234935] via-[#1C3A2B] to-[#0F1C14] p-8 text-white">
        <div aria-hidden className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C5A059]/20 blur-[80px]" />
        <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#C5A059] font-medium flex items-center gap-2">
              <LayoutGrid className="h-3 w-3" /> Operação
            </div>
            <h1 className="mt-2 font-display text-3xl md:text-[34px] font-semibold tracking-tight">
              Quadro de Trabalho por Escala
            </h1>
            <p className="mt-2 text-[13.5px] text-white/70 max-w-[720px]">
              Painel operacional de distribuição de colaboradores por sub-setor e turno.
              Movimente pessoas com poucos cliques e tenha sincronização automática com o cadastro.
            </p>
          </div>
          {canEdit && (
            <div className="flex gap-2 self-start md:self-end">
              <Button onClick={runAutoSync} variant="outline"
                      className="border-white/30 bg-white/10 text-white hover:bg-white/20"
                      data-testid="wb-auto-sync-btn"
                      disabled={autoBusy}>
                <RefreshCw className={`h-4 w-4 mr-1.5 ${autoBusy ? "animate-spin" : ""}`} />
                {autoBusy ? "Sincronizando..." : "Abrir vagas auto."}
              </Button>
              <Button onClick={openNew} className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]" data-testid="wb-new-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Novo quadro
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Grid of boards */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-44 rounded-xl" />)}
        </div>
      ) : boards.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-14 text-center" data-testid="wb-empty">
          <Grid3x3 className="h-9 w-9 text-[#C5A059] mx-auto mb-3" />
          <div className="font-display text-xl font-semibold">Nenhum quadro criado ainda</div>
          <p className="mt-2 text-[13px] text-muted-foreground max-w-[420px] mx-auto">
            Crie o primeiro quadro para começar a distribuir colaboradores por sub-setor e turno.
          </p>
          {canEdit && (
            <Button onClick={openNew} className="mt-6 bg-[#234935] hover:bg-[#2c5c43] text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Criar primeiro quadro
            </Button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {boards.map(b => (
            <div
              key={b.id}
              data-testid={`wb-card-${b.id}`}
              className="group rounded-xl border border-border bg-white p-5 hover:border-[#C5A059]/50 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10.5px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
                    {b.sector_name || "—"}
                  </div>
                  <h3 className="mt-1 font-display text-[17px] font-semibold tracking-tight truncate">
                    {b.name}
                  </h3>
                </div>
                <Badge variant="outline" className="border-[#C5A059]/40 text-[#8A6B2B] bg-[#C5A059]/5 shrink-0">
                  {b.shift_mode === "FOUR_SHIFTS" ? "4 turnos" : "3 turnos"}
                </Badge>
              </div>
              {b.description && (
                <p className="mt-2 text-[12.5px] text-muted-foreground line-clamp-2">{b.description}</p>
              )}
              <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                <div className="rounded-lg bg-slate-50 border border-border p-2.5">
                  <div className="font-display text-xl font-semibold">{b.employees_count || 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Colab.</div>
                </div>
                <div className="rounded-lg bg-slate-50 border border-border p-2.5">
                  <div className="font-display text-xl font-semibold">{b.subsectors_count || 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sub-setores</div>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => nav(`/quadros-trabalho/${b.id}`)}
                  className="bg-[#234935] hover:bg-[#2c5c43] text-white flex-1"
                  data-testid={`wb-open-${b.id}`}
                >
                  Abrir quadro <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
                {canEdit && (
                  <Button variant="outline" size="icon" onClick={() => openEdit(b)} data-testid={`wb-edit-${b.id}`} title="Editar">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button variant="outline" size="icon"
                        onClick={() => downloadPdf(b)}
                        title="Gerar Relatório PDF"
                        className="text-[#234935] hover:bg-[#234935]/5"
                        data-testid={`wb-pdf-${b.id}`}>
                  <FileText className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="icon"
                        onClick={() => downloadDocx(b)}
                        title="Gerar Relatório Word (.docx)"
                        className="text-[#1E40AF] hover:bg-[#1E40AF]/5"
                        data-testid={`wb-docx-${b.id}`}>
                  <FileType2 className="h-3.5 w-3.5" />
                </Button>
                {canAdmin && (
                  <Button variant="outline" size="icon" onClick={() => remove(b)} className="text-amber-600 hover:bg-amber-50" title="Inativar quadro" data-testid={`wb-deactivate-${b.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canAdmin && (
                  <Button variant="outline" size="icon" onClick={() => hardRemove(b)} className="text-rose-700 hover:bg-rose-50" title="Excluir quadro (definitivo)" data-testid={`wb-hard-delete-${b.id}`}>
                    <XCircle className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog new/edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar quadro" : "Novo quadro de trabalho"}</DialogTitle>
            <DialogDescription>
              Defina nome, setor e quantos turnos o quadro comporta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Nome</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex.: Quadro Operacional – Armazém"
                data-testid="wb-form-name"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Setor</Label>
              <Select value={form.sector_id} onValueChange={(v) => setForm({ ...form, sector_id: v })}>
                <SelectTrigger data-testid="wb-form-sector"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {sectors.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Turnos do quadro</Label>
              <Select value={form.shift_mode} onValueChange={(v) => setForm({ ...form, shift_mode: v })}>
                <SelectTrigger data-testid="wb-form-shiftmode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="THREE_SHIFTS">3 turnos (A, B, C) + Administrativo</SelectItem>
                  <SelectItem value="FOUR_SHIFTS">4 turnos (A, B, C, D) + Administrativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Descrição (opcional)</Label>
              <Textarea
                rows={3}
                value={form.description || ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Objetivo e observações deste quadro"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#234935] hover:bg-[#2c5c43] text-white" data-testid="wb-form-save">
              {editing ? "Salvar alterações" : "Criar quadro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
